import {
  CollectionCompatibilityMode,
  CollectionRole,
} from '@qkindex/contracts';

export type CollectionMetricName =
  | 'collection-session-resolution'
  | 'collection-subscription-start'
  | 'collection-subscription-stop'
  | 'collection-subscription-error'
  | 'collection-switch-latency'
  | 'inventory-route'
  | 'membership-version-mismatch'
  | 'cross-collection-path-rejection'
  | 'terminal-offline-revocation';

export interface CollectionMetricMetadata {
  mode?: CollectionCompatibilityMode;
  role?: CollectionRole;
  source?:
    | 'items'
    | 'locations'
    | 'deleted-items'
    | 'rapid-capture'
    | 'membership-listener';
  outcome?: 'ready' | 'unavailable' | 'revoked' | 'failed' | 'changed';
}

const allowedModes = new Set(['collection-only']);
const allowedRoles = new Set(['owner', 'administrator', 'editor', 'viewer']);
const allowedSources = new Set([
  'items',
  'locations',
  'deleted-items',
  'rapid-capture',
  'membership-listener',
]);
const allowedOutcomes = new Set([
  'ready',
  'unavailable',
  'revoked',
  'failed',
  'changed',
]);

const redactedMetadata = (
  metadata: CollectionMetricMetadata,
): CollectionMetricMetadata => ({
  ...(metadata.mode && allowedModes.has(metadata.mode)
    ? { mode: metadata.mode }
    : {}),
  ...(metadata.role && allowedRoles.has(metadata.role)
    ? { role: metadata.role }
    : {}),
  ...(metadata.source && allowedSources.has(metadata.source)
    ? { source: metadata.source }
    : {}),
  ...(metadata.outcome && allowedOutcomes.has(metadata.outcome)
    ? { outcome: metadata.outcome }
    : {}),
});

export const recordCollectionMetric = (
  name: CollectionMetricName,
  value: number,
  metadata: CollectionMetricMetadata = {},
): void => {
  if (!Number.isFinite(value) || value < 0) return;
  const detail = { name, value, metadata: redactedMetadata(metadata) };
  try {
    if (
      typeof performance !== 'undefined' &&
      typeof performance.mark === 'function'
    ) {
      performance.mark(`collection:${name}`, { detail });
    }
  } catch {
    // Metrics must never interrupt inventory behavior.
  }
  try {
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('qkindex:collection-metric', {
          detail,
        }),
      );
    }
  } catch {
    // Metrics must never interrupt inventory behavior.
  }
};
